import {
    EmployerInviteDateState,
    EmployerInviteDateResult
} from "@/employer/types";
import * as UserService from "@/shared/api/userService";
import moment from "moment";
import { FetchState } from "@/shared/types";
import { defineStore } from "pinia";
import _ from "lodash";

const dateFormat = "YYYY-MM-DDTHH:mm";

export const initialState: EmployerInviteDateState = {
    earliestInviteDateMomentUtc: null,
    result: null,
    EmployerInviteDateResultState: FetchState.Uninitialized
};

export const useEmployeeInviteDateStore = defineStore("employeeInviteDate", {
    state: (): EmployerInviteDateState => (_.cloneDeep(initialState)),
    getters: {
        earliestInviteDateLocalString: state => state.earliestInviteDateMomentUtc ?
            moment(state.earliestInviteDateMomentUtc).local().format(dateFormat) : "",
        isEmployerActivated: state => {
            if (state.result) {
                return state.result.isEmployerActivated;
            }

            return false;
        },
        hasSeverValueForInviteDate: state => !!(state.result && state.result.earliestInviteSendDate),
        isFetchStatusLoaded: state => state.EmployerInviteDateResultState === FetchState.Loaded,
        isEmailSendPending: state => {
            if (state.result) {
                return state.result.earliestInviteSendDate === null;
            }
            return false;
        },
        initialEmailSentDate () {
            if (this.result) {
                return this.result.initialEmailSentDate;
            }
            return null;
        },
        canEditInviteDate () {
            if (this.isFetchStatusLoaded) {
                // if we haven't activated you yet with UMB
                // we have no reason to disable scheduling
                if (!this.isEmployerActivated) {
                    return true;
                }
                // if the initial email already sent its now too late
                // to set the date for which you prefer that happens (it is past event)
                if (this.result.initialEmailSentDate) {
                    return false;
                }

                // if you set a date 0-10 minutes from now you're locked out
                // because we may have started processing that.
                const earliestInviteServerValue = this.result.earliestInviteSendDate;
                if (earliestInviteServerValue) {
                    const isInLockoutTime = moment().utc().add(10, "minutes")
                        .isAfter(moment.utc(earliestInviteServerValue));
                    return !isInLockoutTime;
                }

                // given passed all the above checks it can be editted
                return true;
            }
            // lock until loading to complete
            return false;
        },
    },
    actions: {
        setInviteDateInfo (result: EmployerInviteDateResult): void {
            this.result = result;
            if (result.earliestInviteSendDate === null) {
                // default proposed date
                this.earliestInviteDateMomentUtc = moment.utc();
            } else {
                this.earliestInviteDateMomentUtc = moment.utc(result.earliestInviteSendDate);
            }
            this.EmployerInviteDateResultState = FetchState.Loaded;
        },
        setInviteDate (dateMoment: moment.Moment): void {
            if (dateMoment.isValid() === false) {
                return;
            }
            this.earliestInviteDateMomentUtc = dateMoment.utc();
        },
        setEmployerInviteStateLoading (): void {
            this.EmployerInviteDateResultState = FetchState.Loading;
        },
        getEarliestInviteDate (
            employerId: number): Promise<EmployerInviteDateResult> {
            return new Promise((resolve, reject) => {
                if (this.EmployerInviteDateResultState === FetchState.Loaded) {
                    return;
                }
                this.setEmployerInviteStateLoading();
                UserService.getEmployerEarliestInviteDate(employerId).then(response => {
                    this.setInviteDateInfo(response.data);
                    resolve(<EmployerInviteDateResult>response.data);
                }).catch(err => {
                    if (err.response.status === 404) {
                        resolve(null);
                        return;
                    }

                    reject({ message: "User api returned non-OK status", payload: err });
                });
            });
        },
        setEarliestInviteDate (
            employerId: number): Promise<EmployerInviteDateResult> {
            const request = {
                employerId,
                earliestInviteSendDate: moment.utc(this.earliestInviteDateMomentUtc).format(dateFormat)
            };
            return new Promise((resolve, reject) => {
                UserService.updateEmployerEarliestInviteDate(request.employerId, request).then(response => {
                    this.setInviteDateInfo(response.data);
                    resolve(<EmployerInviteDateResult>response.data);
                }).catch(err => reject({ message: "User api returned non-OK status", payload: err }));
            });
        }
    }
});